import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import FieldGroup from '../Form/FieldGroup';
import FloatingLabelFormField from '../../material/FloatingLabelFormField';
import DateTimePickerControl from '../Form/Controls/DateTimePickerControl';
import BaseFilter from './BaseFilter';
import i18n from '../../i18n/translate';
import IconCategoryValue from '../IconCategoryValue';
import Button from '../Button';
import moment from 'moment';
import { pickDataAttributes } from '../../Util';
import { debounce } from 'lodash';

const DateFilter = props => {
  const { onChange, value, fromDateCaption, toDateCaption, isWrapper, tileDisabled } = props;
  const [selectedValue, setSelectedValue] = useState(() => { return value ? value : null });
  const [minDate, setMinDate] = useState(() => { return value && value.from_date ? moment(value.from_date).toDate() : undefined });
  const [maxDate, setMaxDate] = useState(() => { return value && value.to_date ? moment(value.to_date).toDate() : undefined });

  const dataAttributes = pickDataAttributes(props, 'date-filter');

  useEffect(() => {
    if(value !== selectedValue) {
      setValue(value);
    }
  }, [value]);

  const setValue = useCallback(
    debounce(value => {
      setSelectedValue(value);
      setValidations(value);
    }, 500),
    []
  );

  useEffect(() => {
    setValidations(selectedValue);
  }, [selectedValue]);

  const clearFilter = () => {
    setSelectedValue(null);
    if(typeof onChange === "function"){
      onChange(null);
    }
  }

  const setValidations = val => {
    let from_date = val && val.from_date;
    let to_date = val && val.to_date;
    if (from_date) {
      setMinDate(moment(from_date).toDate())
    } else {
      setMinDate(undefined);
    }

    if (to_date) {
      setMaxDate(moment(to_date).toDate())
    } else {
      setMaxDate(undefined);
    }
  }

  const handleValueChange = val => {
    setSelectedValue(prevSelectedValue => {
      return { ...prevSelectedValue, ...val }
    });

    if (typeof onChange === "function") {
      onChange(val);
    }
  }

  return (
    <>
      <FieldGroup className="date-filter">
        <BaseFilter {...props} value={selectedValue} {...dataAttributes}>
          <FieldGroup onValueChange={handleValueChange} data={selectedValue}>
            <FloatingLabelFormField caption={fromDateCaption} className="icon-label" name="from_date">
              <DateTimePickerControl
                tileDisabled={tileDisabled && tileDisabled.fromDate}
                timeFormat={false}
                valueOverride={true}
                maxDate={maxDate}
              />
            </FloatingLabelFormField>
          </FieldGroup>
          <IconCategoryValue icon="arrow-forward" />
          <FieldGroup onValueChange={handleValueChange} data={selectedValue}>
            <FloatingLabelFormField caption={toDateCaption} className="icon-label" name="to_date">
              <DateTimePickerControl
                tileDisabled={tileDisabled && tileDisabled.toDate}
                timeFormat={false}
                valueOverride={true}
                minDate={minDate}
              />
            </FloatingLabelFormField>
          </FieldGroup>
          { selectedValue && (selectedValue.from_date || selectedValue.to_date) && !isWrapper ?
            <Button
              onClick={clearFilter}
              style="link"
              className="clear-filter"
              data-action="clear-filter"
              icon="close"
              label={i18n.t('Clear Filter')}
            /> : null }
        </BaseFilter>
      </FieldGroup>
    </>
  );
};

DateFilter.propTypes = {
  fromDateCaption: PropTypes.string,
  isWrapper: PropTypes.bool,
  label: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  tileDisabled: PropTypes.shape({
    fromDate: PropTypes.func,
    toDate: PropTypes.func
  }),
  toDateCaption: PropTypes.string,
  value: PropTypes.object
};

DateFilter.defaultProps = {
  fromDateCaption: i18n.t("From Date"),
  isWrapper: false,
  toDateCaption: i18n.t("To Date")
}

export default DateFilter;
